import React from 'react';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Cake, Bell, Eye, EyeOff } from 'lucide-react';
import {
  monthOptions,
  daysInMonth,
  type BirthdayVisibility,
} from '@/lib/birthday';

export type BirthdayMode = 'full' | 'month_day' | 'year' | 'unknown';

export interface BirthdayValue {
  mode: BirthdayMode;
  month: number | null; // 1-12
  day: number | null;   // 1-31
  year: number | null;
  visibility: BirthdayVisibility;
  reminders: boolean;
}

interface Props {
  value: BirthdayValue;
  onChange: (next: BirthdayValue) => void;
  // Lighter visual variant inside drawer
  compact?: boolean;
}

export const emptyBirthday = (): BirthdayValue => ({
  mode: 'unknown',
  month: null,
  day: null,
  year: null,
  visibility: 'public',
  reminders: true,
});

// Convert DB-shaped fields into a BirthdayValue (used by drawer for editing).
export const birthdayFromMember = (m: {
  birthDate?: string | null;
  birthYear?: number | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  birthdayVisibility?: BirthdayVisibility;
  birthdayReminders?: boolean;
}): BirthdayValue => {
  let month = m.birthMonth ?? null;
  let day = m.birthDay ?? null;
  let year = m.birthYear ?? null;
  if (m.birthDate) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(m.birthDate);
    if (match) {
      year = year ?? parseInt(match[1], 10);
      month = month ?? parseInt(match[2], 10);
      day = day ?? parseInt(match[3], 10);
    }
  }
  let mode: BirthdayMode = 'unknown';
  if (month && day && year) mode = 'full';
  else if (month && day) mode = 'month_day';
  else if (year) mode = 'year';
  return {
    mode,
    month,
    day,
    year,
    visibility: m.birthdayVisibility ?? 'public',
    reminders: m.birthdayReminders ?? true,
  };
};

// Build the DB payload (birthDate/birthMonth/birthDay/birthYear) from the
// editor value. Always returns explicit nulls so existing values can be cleared.
export const birthdayToPayload = (v: BirthdayValue) => {
  if (v.mode === 'unknown') {
    return {
      birthDate: null,
      birthMonth: null,
      birthDay: null,
      birthYear: null,
      birthdayVisibility: v.visibility,
      birthdayReminders: v.reminders,
    };
  }
  if (v.mode === 'full' && v.month && v.day && v.year) {
    const mm = String(v.month).padStart(2, '0');
    const dd = String(v.day).padStart(2, '0');
    return {
      birthDate: `${v.year}-${mm}-${dd}`,
      birthMonth: v.month,
      birthDay: v.day,
      birthYear: v.year,
      birthdayVisibility: v.visibility,
      birthdayReminders: v.reminders,
    };
  }
  if (v.mode === 'month_day' && v.month && v.day) {
    return {
      birthDate: null,
      birthMonth: v.month,
      birthDay: v.day,
      birthYear: null,
      birthdayVisibility: v.visibility,
      birthdayReminders: v.reminders,
    };
  }
  if (v.mode === 'year' && v.year) {
    return {
      birthDate: null,
      birthMonth: null,
      birthDay: null,
      birthYear: v.year,
      birthdayVisibility: v.visibility,
      birthdayReminders: v.reminders,
    };
  }
  // Mode set but values incomplete → store nothing
  return {
    birthDate: null,
    birthMonth: null,
    birthDay: null,
    birthYear: null,
    birthdayVisibility: v.visibility,
    birthdayReminders: v.reminders,
  };
};

/**
 * formatDobDigits — pure DOB digit-buffer → MM/DD/YYYY formatter.
 *
 * Single source of truth for the auto-slash behaviour required across
 * EVERY DOB input in the app (onboarding, add-relative, edit-profile,
 * profile drawer, member forms).
 *
 * Contract (exactly per the spec):
 *   - User types digits only.
 *   - After 2 digits a `/` is auto-inserted.
 *   - After 4 digits a second `/` is auto-inserted.
 *   - Backspace works naturally (we never insert a slash unless the digit
 *     before it is actually present).
 *   - Max 8 digits / 10 formatted chars.
 *
 * Examples (mirror the user-supplied table):
 *   ""       → ""
 *   "0"      → "0"
 *   "04"     → "04/"
 *   "042"    → "04/2"
 *   "0426"   → "04/26/"
 *   "04261"  → "04/26/1"
 *   "04261990" → "04/26/1990"
 *
 * Pure function — does NOT touch the DB schema, the stored birthday triple
 * (year/month/day), or any validation. The caller decides when to commit
 * the formatted value to the underlying month/day/year fields.
 */
export const formatDobDigits = (digits: string): string => {
  // Strip every non-digit and cap to 8.
  const d = (digits ?? '').replace(/\D+/g, '').slice(0, 8);
  if (d.length === 0) return '';
  if (d.length === 1) return d;            // "0"
  if (d.length === 2) return `${d}/`;       // "04/"
  if (d.length === 3) return `${d.slice(0, 2)}/${d.slice(2, 3)}`; // "04/2"
  if (d.length === 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/`; // "04/26/"
  // length 5..8 — fill the year portion as the user types.
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4, 8)}`;
};

/**
 * parseDobText — read a partially-typed MM/DD/YYYY string back into the
 * shared month/day/year/digit triple. Mirrors `formatDobDigits` so the
 * formatted display is always reversible. Used by every DOB text input
 * to persist what the user actually typed.
 */
export const parseDobText = (
  raw: string,
): { digits: string; month: number | null; day: number | null; year: number | null } => {
  const digits = (raw ?? '').replace(/\D+/g, '').slice(0, 8);
  let month: number | null = null;
  let day: number | null = null;
  let year: number | null = null;
  if (digits.length >= 2) month = parseInt(digits.slice(0, 2), 10);
  if (digits.length >= 4) day = parseInt(digits.slice(2, 4), 10);
  if (digits.length === 8) year = parseInt(digits.slice(4, 8), 10);
  return { digits, month, day, year };
};

/**
 * validateDob — required validation per the spec:
 *   - month 01–12
 *   - day 01–31 (and must be a real calendar day given the month/year)
 *   - year 1800–current year
 * Returns null when valid, or a human-readable error string when not.
 */
export const validateDob = (
  month: number | null,
  day: number | null,
  year: number | null,
): string | null => {
  if (month === null && day === null && year === null) return null; // empty = no-op
  if (month === null || day === null || year === null) return 'Enter MM/DD/YYYY.';
  if (month < 1 || month > 12) return 'Month must be 01–12.';
  const dCap = daysInMonth(month, year);
  if (day < 1 || day > dCap) return `Day must be 01–${dCap}.`;
  const nowYear = new Date().getFullYear();
  if (year < 1800 || year > nowYear) return `Year must be 1800–${nowYear}.`;
  return null;
};

const BirthdayInput: React.FC<Props> = ({ value, onChange, compact = false }) => {
  const set = (patch: Partial<BirthdayValue>) => onChange({ ...value, ...patch });

  const showMonthDay = value.mode === 'full' || value.mode === 'month_day';
  const showYear = value.mode === 'full' || value.mode === 'year';

  const dayCap = value.month ? daysInMonth(value.month, value.year) : 31;

  // ── DOB MOBILE UX (REWRITTEN May 2026 — auto-slash that actually works) ──
  //
  // Previously this component derived the on-screen text from the
  // value.year/month/day triple. That meant typing the first digit ("0")
  // produced an empty display (because month was still null until two
  // digits were typed), so the auto-slash behaviour was invisible to the
  // user — exactly the production bug reported.
  //
  // The rewrite keeps a LOCAL `manualDigits` buffer that tracks every
  // keystroke. `formatDobDigits()` (pure helper above) turns that buffer
  // into the MM/DD/YYYY display string with the slashes inserted at the
  // 2nd and 4th positions. The buffer is the source of truth WHILE the
  // user is typing; on every change we also commit month/day/year to the
  // shared value via onChange so the existing storage triple stays in
  // lockstep. When the parent props update the triple externally
  // (e.g. the native date picker fires, the modal opens with a pre-
  // existing birthday loaded), we re-seed `manualDigits` from those
  // values so the two stay in sync.
  //
  // Storage triple (year/month/day) is unchanged — same DB columns,
  // same payload helper. This is purely a presentation rewrite.
  const [manualDigits, setManualDigits] = React.useState<string>(() => {
    const mm = value.month ? String(value.month).padStart(2, '0') : '';
    const dd = value.day ? String(value.day).padStart(2, '0') : '';
    const yyyy = value.year ? String(value.year).padStart(4, '0') : '';
    return `${mm}${dd}${yyyy}`;
  });

  // Re-seed the digit buffer whenever the underlying value triple is
  // changed by a DIFFERENT path (native picker, mode switch, parent
  // re-render with a freshly loaded member). We only re-seed when the
  // triple disagrees with what the buffer would format to — otherwise
  // every keystroke would round-trip through onChange and reset the
  // buffer mid-typing, jumping the caret to the end.
  React.useEffect(() => {
    if (value.mode !== 'full') {
      // For non-full modes, keep digits cleared so switching back to
      // full mode starts fresh.
      if (manualDigits !== '') setManualDigits('');
      return;
    }
    const expectedMm = value.month ? String(value.month).padStart(2, '0') : '';
    const expectedDd = value.day ? String(value.day).padStart(2, '0') : '';
    const expectedYyyy = value.year ? String(value.year).padStart(4, '0') : '';
    const expected = `${expectedMm}${expectedDd}${expectedYyyy}`;
    // Buffer is "ahead" of the triple while the user is mid-type
    // (e.g. they typed 3 digits but month/day aren't fully set). Only
    // re-seed when the triple has a fully-resolved value that the
    // buffer doesn't reflect.
    const bufferTriple = (() => {
      const d = manualDigits.replace(/\D+/g, '');
      const mm = d.length >= 2 ? d.slice(0, 2) : '';
      const dd = d.length >= 4 ? d.slice(2, 4) : '';
      const yy = d.length === 8 ? d.slice(4, 8) : '';
      return `${mm}${dd}${yy}`;
    })();
    if (expected && expected !== bufferTriple) {
      setManualDigits(expected.replace(/\D+/g, ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.mode, value.month, value.day, value.year]);

  const nativeDateValue = (() => {
    if (value.mode !== 'full') return '';
    if (!value.year || !value.month || !value.day) return '';
    const mm = String(value.month).padStart(2, '0');
    const dd = String(value.day).padStart(2, '0');
    return `${value.year}-${mm}-${dd}`;
  })();
  const todayIso = new Date().toISOString().slice(0, 10);

  const handleNativeDateChange = (raw: string) => {
    if (!raw) {
      setManualDigits('');
      set({ year: null, month: null, day: null });
      return;
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (!m) return;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return;
    const dCap = daysInMonth(mo, y);
    const dClamped = Math.min(Math.max(d, 1), dCap);
    setManualDigits(
      `${String(mo).padStart(2, '0')}${String(dClamped).padStart(2, '0')}${String(y).padStart(4, '0')}`,
    );
    set({ year: y, month: mo, day: dClamped });
  };

  // Validation message shown beneath the manual text field. Pure
  // presentation — never blocks the user mid-typing. The save handler
  // upstream is responsible for refusing to persist an invalid date
  // (see EditProfileModal's mdyToIso for the existing guard).
  const manualText = formatDobDigits(manualDigits);
  const parsed = parseDobText(manualText);
  const manualError =
    manualDigits.length === 8 ? validateDob(parsed.month, parsed.day, parsed.year) : null;

  const handleManualText = (raw: string) => {
    // Accept whatever the user typed (or pasted). Strip to digits,
    // cap at 8, then push BOTH the buffer (for display) and the
    // shared month/day/year (for storage).
    const next = (raw ?? '').replace(/\D+/g, '').slice(0, 8);
    setManualDigits(next);
    // Mid-typing we propagate partial values so the shared payload is
    // always in lockstep. The save guard above blocks invalid commits.
    let mm: number | null = null;
    let dd: number | null = null;
    let yy: number | null = null;
    if (next.length >= 2) mm = parseInt(next.slice(0, 2), 10);
    if (next.length >= 4) dd = parseInt(next.slice(2, 4), 10);
    if (next.length === 8) yy = parseInt(next.slice(4, 8), 10);
    // Clamp obvious overflow at the digit-boundary so the user can't
    // commit "Feb 31" or month=13 — preserves the previous validation
    // hardening at the data layer.
    if (mm !== null && mm > 12) mm = 12;
    if (mm !== null && mm < 1 && next.length >= 2) mm = 1;
    if (dd !== null && mm) {
      const dCap = daysInMonth(mm, yy ?? null);
      if (dd > dCap) dd = dCap;
      if (dd < 1 && next.length >= 4) dd = 1;
    }
    if (yy !== null) {
      const nowYear = new Date().getFullYear();
      if (yy < 1800) yy = null;
      else if (yy > nowYear) yy = nowYear;
    }
    set({ month: mm, day: dd, year: yy });
  };

  return (
    <div className={`rounded-xl border ${compact ? 'border-slate-200 p-3' : 'border-fuchsia-200 bg-fuchsia-50/40 p-4'} space-y-3`}>
      <div className="flex items-center gap-2">
        <Cake className="w-4 h-4 text-fuchsia-600" />
        <Label className="text-sm font-bold text-slate-800 m-0">Date of birth</Label>
      </div>

      <div>
        <Label className="text-xs text-slate-600">How much do you know?</Label>
        <Select
          value={value.mode}
          onValueChange={(v) => {
            const next = v as BirthdayMode;
            // Clear fields that aren't relevant to the new mode
            const cleaned: Partial<BirthdayValue> = { mode: next };
            if (next === 'unknown') { cleaned.month = null; cleaned.day = null; cleaned.year = null; }
            if (next === 'year') { cleaned.month = null; cleaned.day = null; }
            if (next === 'month_day') { cleaned.year = null; }
            set(cleaned);
          }}
        >
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="full">Full date — Month / Day / Year</SelectItem>
            <SelectItem value="month_day">Month &amp; day only</SelectItem>
            <SelectItem value="year">Year only</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ── Full-date mobile-friendly inputs ── */}
      {value.mode === 'full' && (
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-slate-600">Pick a date</Label>
            {/*
             * Native <input type="date"> opens the OS calendar / wheel
             * picker on Android (Chrome / Samsung / Firefox), iPhone /
             * iPad Safari, and recent desktop browsers. min/max keep the
             * range sensible.
             */}
            <Input
              type="date"
              value={nativeDateValue}
              min="1900-01-01"
              max={todayIso}
              onChange={(e) => handleNativeDateChange(e.target.value)}
              className="mt-1 font-mono"
              aria-label="Date of birth (date picker)"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Tap to open your phone's date picker — no need to type slashes.
            </p>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Or type it (MM/DD/YYYY)</Label>
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="bday"
              placeholder="MM/DD/YYYY"
              value={manualText}
              onChange={(e) => handleManualText(e.target.value)}
              maxLength={10}
              className="mt-1 font-mono tracking-wider"
              aria-label="Date of birth (manual entry)"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Slashes appear automatically as you type.
            </p>
            {manualError && (
              <p className="text-[11px] font-semibold text-rose-600 mt-1">
                {manualError}
              </p>
            )}
          </div>
        </div>
      )}

      {(value.mode === 'month_day' || value.mode === 'year') && (showMonthDay || showYear) && (
        <div className="grid grid-cols-3 gap-2">
          {showMonthDay && (
            <>
              <div>
                <Label className="text-xs text-slate-600">Month</Label>
                <Select
                  value={value.month ? String(value.month) : ''}
                  onValueChange={(v) => set({ month: parseInt(v, 10) })}
                >
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Month" /></SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Day</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={dayCap}
                  value={value.day ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') return set({ day: null });
                    const n = parseInt(raw, 10);
                    if (Number.isFinite(n)) set({ day: Math.min(Math.max(n, 1), dayCap) });
                  }}
                  placeholder="DD"
                  className="mt-1"
                />
              </div>
            </>
          )}
          {showYear && (
            <div className={showMonthDay ? '' : 'col-span-3'}>
              <Label className="text-xs text-slate-600">Year</Label>
              <Input
                type="number"
                inputMode="numeric"
                min={1800}
                max={new Date().getFullYear()}
                value={value.year ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') return set({ year: null });
                  const n = parseInt(raw, 10);
                  if (Number.isFinite(n)) set({ year: n });
                }}
                placeholder="YYYY"
                className="mt-1"
              />
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600 flex items-center gap-1">
            {value.visibility === 'private' ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            Birthday visibility
          </Label>
          <Select
            value={value.visibility}
            onValueChange={(v) => set({ visibility: v as BirthdayVisibility })}
          >
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public to family</SelectItem>
              <SelectItem value="year_only">Year only (hide month &amp; day)</SelectItem>
              <SelectItem value="private">Private (hidden from everyone)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 w-full bg-white rounded-lg border border-slate-200 px-3 py-2 cursor-pointer">
            <Bell className="w-4 h-4 text-amber-500" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-slate-800">Birthday reminders</div>
              <div className="text-[11px] text-slate-500">Notify family when this birthday is coming up</div>
            </div>
            <Switch
              checked={value.reminders}
              onCheckedChange={(c) => set({ reminders: c })}
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default BirthdayInput;
